import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import {
  WebhooksConfiguration,
  WebhookPayload,
  WebhookTestResult,
  WebhookEventType,
} from "@/types/webhooks.types";

const WEBHOOKS_CONFIG_PATH = "config/webhooks";

const normalizeWebhookUrl = (value: string): string => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

/**
 * Default webhook configuration
 */
const DEFAULT_WEBHOOK_CONFIG: WebhooksConfiguration = {
  claimSubmitted: {
    url: "",
    enabled: false,
    description: "Webhook wird getriggert wenn ein neuer Claim eingereicht wird",
  },
  claimApproved: {
    url: "",
    enabled: false,
    description:
      "Webhook wird getriggert wenn ein Claim vom Admin genehmigt wird",
  },
  claimRejected: {
    url: "",
    enabled: false,
    description:
      "Webhook wird getriggert wenn ein Claim vom Admin abgelehnt wird",
  },
  ownerInactive: {
    url: "",
    enabled: false,
    description:
      "Täglich getriggert für Owner, die ihre Bar seit 7+ Tagen nicht aktualisiert haben",
  },
  trialExpiringSoon: {
    url: "",
    enabled: false,
    description:
      "Getriggert wenn der Trial-Zeitraum eines Owners in 3 Tagen abläuft",
  },
  trialExpired: {
    url: "",
    enabled: false,
    description:
      "Getriggert wenn der Trial-Zeitraum eines Owners abgelaufen ist",
  },
  trialExtended: {
    url: "",
    enabled: false,
    description:
      "Getriggert wenn ein Admin den Trial-Zeitraum eines Owners verlängert hat",
  },
  subscriptionActivated: {
    url: "",
    enabled: false,
    description: "Getriggert wenn ein Owner sein Abo erfolgreich abgeschlossen hat",
  },
  paymentFailed: {
    url: "",
    enabled: false,
    description: "Getriggert wenn eine Stripe-Zahlung fehlschlägt",
  },
  ownerWelcome: {
    url: "",
    enabled: false,
    description: "Getriggert wenn ein Owner erstmals sein Trial startet (Willkommens-E-Mail)",
  },
  barVerified: {
    url: "",
    enabled: false,
    description: "Getriggert wenn eine Bar vom Admin verifiziert wird",
  },
  coOwnerInvited: {
    url: "",
    enabled: false,
    description: "Getriggert wenn ein Owner jemanden als Co-Owner einlädt",
  },
  monthlyBarStats: {
    url: "",
    enabled: false,
    description: "Am 1. jeden Monats: Monatsstatistiken pro Bar (Follower, Views, Check-ins)",
  },
};

/**
 * Webhooks Service
 * Handles webhook configuration and triggering
 */
export const webhooksService = {
  /**
   * Get webhook configuration from Firestore
   */
  getWebhookConfig: async (): Promise<WebhooksConfiguration> => {
    try {
      const docRef = doc(db, WEBHOOKS_CONFIG_PATH);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          ...DEFAULT_WEBHOOK_CONFIG,
          ...docSnap.data(),
          claimSubmitted: {
            ...DEFAULT_WEBHOOK_CONFIG.claimSubmitted,
            ...(docSnap.data().claimSubmitted ?? {}),
          },
          claimApproved: {
            ...DEFAULT_WEBHOOK_CONFIG.claimApproved,
            ...(docSnap.data().claimApproved ?? {}),
          },
          claimRejected: {
            ...DEFAULT_WEBHOOK_CONFIG.claimRejected,
            ...(docSnap.data().claimRejected ?? {}),
          },
          ownerInactive: {
            ...DEFAULT_WEBHOOK_CONFIG.ownerInactive,
            ...(docSnap.data().ownerInactive ?? {}),
          },
          trialExpiringSoon: {
            ...DEFAULT_WEBHOOK_CONFIG.trialExpiringSoon,
            ...(docSnap.data().trialExpiringSoon ?? {}),
          },
          trialExpired: {
            ...DEFAULT_WEBHOOK_CONFIG.trialExpired,
            ...(docSnap.data().trialExpired ?? {}),
          },
          trialExtended: {
            ...DEFAULT_WEBHOOK_CONFIG.trialExtended,
            ...(docSnap.data().trialExtended ?? {}),
          },
          subscriptionActivated: {
            ...DEFAULT_WEBHOOK_CONFIG.subscriptionActivated,
            ...(docSnap.data().subscriptionActivated ?? {}),
          },
          paymentFailed: {
            ...DEFAULT_WEBHOOK_CONFIG.paymentFailed,
            ...(docSnap.data().paymentFailed ?? {}),
          },
          ownerWelcome: {
            ...DEFAULT_WEBHOOK_CONFIG.ownerWelcome,
            ...(docSnap.data().ownerWelcome ?? {}),
          },
          barVerified: {
            ...DEFAULT_WEBHOOK_CONFIG.barVerified,
            ...(docSnap.data().barVerified ?? {}),
          },
          coOwnerInvited: {
            ...DEFAULT_WEBHOOK_CONFIG.coOwnerInvited,
            ...(docSnap.data().coOwnerInvited ?? {}),
          },
          monthlyBarStats: {
            ...DEFAULT_WEBHOOK_CONFIG.monthlyBarStats,
            ...(docSnap.data().monthlyBarStats ?? {}),
          },
        } as WebhooksConfiguration;
      }

      // Return default config if document doesn't exist
      return DEFAULT_WEBHOOK_CONFIG;
    } catch (error) {
      console.error("Error fetching webhook config:", error);
      throw new Error(
        "Fehler beim Laden der Webhook-Konfiguration: " +
          (error as Error).message,
      );
    }
  },

  /**
   * Update webhook configuration in Firestore
   */
  updateWebhookConfig: async (
    config: WebhooksConfiguration,
    adminUid: string,
  ): Promise<void> => {
    try {
      const docRef = doc(db, WEBHOOKS_CONFIG_PATH);
      const normalizedConfig: WebhooksConfiguration = {
        ...config,
        claimSubmitted: {
          ...config.claimSubmitted,
          url: normalizeWebhookUrl(config.claimSubmitted.url),
        },
        claimApproved: {
          ...config.claimApproved,
          url: normalizeWebhookUrl(config.claimApproved.url),
        },
        claimRejected: {
          ...config.claimRejected,
          url: normalizeWebhookUrl(config.claimRejected.url),
        },
        ownerInactive: {
          ...config.ownerInactive,
          url: normalizeWebhookUrl(config.ownerInactive.url),
        },
        trialExpiringSoon: {
          ...config.trialExpiringSoon,
          url: normalizeWebhookUrl(config.trialExpiringSoon.url),
        },
        trialExpired: {
          ...config.trialExpired,
          url: normalizeWebhookUrl(config.trialExpired.url),
        },
        trialExtended: {
          ...config.trialExtended,
          url: normalizeWebhookUrl(config.trialExtended.url),
        },
        subscriptionActivated: {
          ...config.subscriptionActivated,
          url: normalizeWebhookUrl(config.subscriptionActivated.url),
        },
        paymentFailed: {
          ...config.paymentFailed,
          url: normalizeWebhookUrl(config.paymentFailed.url),
        },
        ownerWelcome: {
          ...config.ownerWelcome,
          url: normalizeWebhookUrl(config.ownerWelcome.url),
        },
        barVerified: {
          ...config.barVerified,
          url: normalizeWebhookUrl(config.barVerified.url),
        },
        coOwnerInvited: {
          ...config.coOwnerInvited,
          url: normalizeWebhookUrl(config.coOwnerInvited.url),
        },
        monthlyBarStats: {
          ...config.monthlyBarStats,
          url: normalizeWebhookUrl(config.monthlyBarStats.url),
        },
      };

      await setDoc(docRef, {
        ...normalizedConfig,
        updatedAt: serverTimestamp(),
        updatedBy: adminUid,
      });
    } catch (error) {
      console.error("Error updating webhook config:", error);
      throw new Error(
        "Fehler beim Speichern der Webhook-Konfiguration: " +
          (error as Error).message,
      );
    }
  },

  /**
   * Test a webhook by sending a test payload
   */
  testWebhook: async (
    url: string,
    eventType: WebhookEventType,
  ): Promise<WebhookTestResult> => {
    const startTime = Date.now();
    const normalizedUrl = normalizeWebhookUrl(url);

    // Create test payload based on event type
    const testPayload: WebhookPayload = (() => {
      const timestamp = new Date().toISOString();

      switch (eventType) {
        case WebhookEventType.CLAIM_SUBMITTED:
          return {
            eventType: WebhookEventType.CLAIM_SUBMITTED,
            timestamp,
            claimId: "test_claim_123",
            barId: "test_bar_123",
            barName: "Test Bar",
            userId: "test_user_123",
            userEmail: "test@example.com",
            userName: "Test User",
            message: "Dies ist ein Test-Claim",
            proofImages: [],
          };

        case WebhookEventType.CLAIM_APPROVED:
          return {
            eventType: WebhookEventType.CLAIM_APPROVED,
            timestamp,
            claimId: "test_claim_123",
            barId: "test_bar_123",
            barName: "Test Bar",
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            approvedBy: "admin_123",
            approvedAt: timestamp,
            trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          };

        case WebhookEventType.CLAIM_REJECTED:
          return {
            eventType: WebhookEventType.CLAIM_REJECTED,
            timestamp,
            claimId: "test_claim_123",
            barId: "test_bar_123",
            barName: "Test Bar",
            userId: "test_user_123",
            userEmail: "test@example.com",
            userName: "Test User",
            rejectedBy: "admin_123",
            rejectedAt: timestamp,
            rejectionReason: "Test-Ablehnung",
          };

        case WebhookEventType.OWNER_INACTIVE:
          return {
            eventType: WebhookEventType.OWNER_INACTIVE,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            lastActivityAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            inactiveDays: 7,
          };

        case WebhookEventType.TRIAL_EXPIRING_SOON:
          return {
            eventType: WebhookEventType.TRIAL_EXPIRING_SOON,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            trialEndsAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
            daysRemaining: 3,
          };

        case WebhookEventType.TRIAL_EXPIRED:
          return {
            eventType: WebhookEventType.TRIAL_EXPIRED,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            trialEndedAt: timestamp,
          };

        case WebhookEventType.TRIAL_EXTENDED:
          return {
            eventType: WebhookEventType.TRIAL_EXTENDED,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            newTrialEndsAt: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
            previousTrialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            extendedByDays: 30,
            extendedByAdminId: "admin_123",
          };

        case WebhookEventType.SUBSCRIPTION_ACTIVATED:
          return {
            eventType: WebhookEventType.SUBSCRIPTION_ACTIVATED,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            activatedAt: timestamp,
            amountPaid: 29.99,
            currency: "eur",
          };

        case WebhookEventType.PAYMENT_FAILED:
          return {
            eventType: WebhookEventType.PAYMENT_FAILED,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            gracePeriodEndsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          };

        case WebhookEventType.OWNER_WELCOME:
          return {
            eventType: WebhookEventType.OWNER_WELCOME,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            trialEndsAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
          };

        case WebhookEventType.BAR_VERIFIED:
          return {
            eventType: WebhookEventType.BAR_VERIFIED,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            verifiedAt: timestamp,
          };

        case WebhookEventType.CO_OWNER_INVITED:
          return {
            eventType: WebhookEventType.CO_OWNER_INVITED,
            timestamp,
            invitedByOwnerId: "test_owner_123",
            invitedByOwnerEmail: "owner@example.com",
            invitedByOwnerName: "Test Owner",
            invitedEmail: "coowner@example.com",
            barId: "test_bar_123",
            barName: "Test Bar",
            invitationToken: "abc123token",
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          };

        case WebhookEventType.MONTHLY_BAR_STATS:
          return {
            eventType: WebhookEventType.MONTHLY_BAR_STATS,
            timestamp,
            ownerId: "test_owner_123",
            ownerEmail: "owner@example.com",
            ownerName: "Test Owner",
            barId: "test_bar_123",
            barName: "Test Bar",
            month: new Date().toISOString().slice(0, 7),
            followerCount: 142,
            checkInCount: 38,
            viewCount: 520,
          };

        default:
          throw new Error(`Unknown event type: ${eventType}`);
      }
    })();

    try {
      const response = await fetch(normalizedUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "User-Agent": "Kneipster-Admin-Dashboard/1.0",
        },
        body: JSON.stringify(testPayload),
      });

      const responseTime = Date.now() - startTime;

      if (response.ok) {
        return {
          success: true,
          statusCode: response.status,
          message: `Webhook erfolgreich getestet (${responseTime}ms)`,
          responseTime,
        };
      } else {
        return {
          success: false,
          statusCode: response.status,
          message: `Webhook fehlgeschlagen: ${response.statusText}`,
          responseTime,
          error: await response.text().catch(() => "No error details available"),
        };
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      return {
        success: false,
        message: `Webhook-Fehler: ${(error as Error).message}`,
        responseTime,
        error: (error as Error).message,
      };
    }
  },

  /**
   * Trigger a webhook (non-blocking)
   * This function is called from the claims service and should not throw errors
   */
  triggerWebhook: async (
    eventType: WebhookEventType,
    payload: WebhookPayload,
  ): Promise<void> => {
    try {
      // Get webhook configuration
      const config = await webhooksService.getWebhookConfig();

      // Determine which webhook to trigger
      let webhookConfig:
        | WebhooksConfiguration["claimSubmitted"]
        | WebhooksConfiguration["claimApproved"]
        | WebhooksConfiguration["claimRejected"];
      switch (eventType) {
        case WebhookEventType.CLAIM_SUBMITTED:
          webhookConfig = config.claimSubmitted;
          break;
        case WebhookEventType.CLAIM_APPROVED:
          webhookConfig = config.claimApproved;
          break;
        case WebhookEventType.CLAIM_REJECTED:
          webhookConfig = config.claimRejected;
          break;
        default:
          return;
      }

      // Check if webhook is enabled and has a URL
      if (!webhookConfig.enabled || !webhookConfig.url) {
        return;
      }

      // Trigger webhook (non-blocking, fire-and-forget)
      fetch(webhookConfig.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "User-Agent": "Kneipster-Admin-Dashboard/1.0",
        },
        body: JSON.stringify(payload),
      })
        .catch(() => {
          // Webhooks sollen den Hauptfluss nicht blockieren.
        });
    } catch {
      // Webhooks sollen den Hauptfluss nicht blockieren.
    }
  },
};
